import { StyleBindings } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { Schema } from '@sapappgyver/appgyver-descriptors/lib/common/schemas';
import { CSSProperties } from 'react';
import { fontWeights, fontStyles } from './fonts';
import getKeys from '../utilities/getKeys';

const fontSizeSchema: Schema = {
  title: 'Font size',
  type: 'number',
  minimum: 0,
};

const lineHeightSchema: Schema = {
  title: 'Line height',
  type: 'number',
  // NOTE: `step` is not a schema keyword and does not affect type validation.
  // It is only used by the UI component for correct stepper
  step: 0.1,
};

const stylePropertySchemas = {
  margin: {
    title: 'Margin',
    type: 'number',
    default: 0,
  },
  marginLeft: {
    title: 'Left margin',
    type: 'number',
    default: 0,
  },
  marginRight: {
    title: 'Right margin',
    type: 'number',
    default: 0,
  },
  marginTop: {
    title: 'Top margin',
    type: 'number',
    default: 0,
  },
  marginBottom: {
    title: 'Bottom margin',
    type: 'number',
    default: 0,
  },
  left: {
    title: 'Left',
    type: 'number',
  },
  right: {
    title: 'Right',
    type: 'number',
  },
  top: {
    title: 'Top',
    type: 'number',
  },
  bottom: {
    title: 'Bottom',
    type: 'number',
  },
  padding: {
    title: 'Padding',
    type: 'number',
    default: 0,
  },
  paddingLeft: {
    title: 'Left padding',
    type: 'number',
    default: 0,
  },
  paddingRight: {
    title: 'Right padding',
    type: 'number',
    default: 0,
  },
  paddingTop: {
    title: 'Top padding',
    type: 'number',
    default: 0,
  },
  paddingBottom: {
    title: 'Bottom padding',
    type: 'number',
    default: 0,
  },
  flex: {
    title: 'Flex',
    type: 'string',
  },
  zIndex: {
    title: 'Z-Index',
    anyOf: [
      {
        type: 'number',
      },
      {
        type: 'string',
        enum: ['auto'],
      },
    ],
  },
  aspectRatio: {
    title: 'Aspect ratio',
    type: 'number',
    minimum: 0,
  },
  elevation: {
    title: 'Elevation',
    type: 'number',
    minimum: 0,
  },
  flexGrow: {
    title: 'Flex grow',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  flexShrink: {
    title: 'Flex shrink',
    type: 'number',
    minimum: 0,
    default: 1,
  },
  textSize: {
    title: 'Text size',
    type: 'object',
    properties: {
      fontSize: fontSizeSchema,
      lineHeight: lineHeightSchema,
    },
  },
  fontSize: fontSizeSchema,
  shadowOpacity: {
    title: 'Shadow opacity',
    type: 'number',
    minimum: 0,
    maximum: 1,
    default: 1,
    // NOTE: `step` is not a schema keyword and does not affect type validation.
    // It is only used by the UI component for correct stepper
    step: 0.1,
  },
  flexBasis: {
    title: 'Flex basis',
    anyOf: [
      { type: 'string', pattern: '^\\d+(?:px|%)?$|^auto$' },
      { type: 'number', minimum: 0 },
    ],
    default: 'auto',
  },
  opacity: {
    title: 'Opacity',
    type: 'number',
    minimum: 0,
    maximum: 1,
    default: 1,
    // NOTE: `step` is not a schema keyword and does not affect type validation.
    // It is only used by the UI component for correct stepper
    step: 0.1,
  },
  lineHeight: lineHeightSchema,
  letterSpacing: {
    title: 'Letter spacing (web, iOS)',
    anyOf: [
      {
        title: 'Normal',
        type: 'string',
        enum: ['normal'],
      },
      {
        title: 'Custom spacing',
        type: 'number',
        // NOTE: `step` is not a schema keyword and does not affect type validation.
        // It is only used by the UI component for correct stepper
        step: 0.1,
      },
    ],
  },
  width: {
    title: 'Width',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  minWidth: {
    title: 'Min width',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  maxWidth: {
    title: 'Max width',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  height: {
    title: 'Height',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  minHeight: {
    title: 'Min height',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  maxHeight: {
    title: 'Max height',
    anyOf: [
      { type: 'string', format: 'percentage' }, // E.g. "55.5%"
      { type: 'string', format: 'number' }, // E.g. "123.5"
      { type: 'number', minimum: 0 },
    ],
  },
  flexDirection: {
    title: 'Flex direction',
    type: 'string',
    enum: ['row', 'row-reverse', 'column', 'column-reverse'],
    default: 'column', // NOTE: This is RN default
  },
  flexWrap: {
    title: 'Flex wrap',
    type: 'string',
    enum: ['wrap', 'nowrap'],
    default: 'nowrap',
  },
  alignContent: {
    title: 'Align content',
    type: 'string',
    enum: ['flex-start', 'flex-end', 'center', 'stretch', 'space-between', 'space-around'],
    default: 'stretch',
  },
  alignItems: {
    title: 'Align items',
    type: 'string',
    enum: ['flex-start', 'flex-end', 'center', 'stretch', 'baseline'],
    default: 'stretch',
  },
  alignSelf: {
    title: 'Align self',
    type: 'string',
    enum: ['auto', 'flex-start', 'flex-end', 'center', 'stretch', 'baseline'],
    default: 'auto',
  },
  justifyContent: {
    title: 'Justify content',
    type: 'string',
    enum: ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'],
    default: 'flex-start',
  },
  placeContent: {
    title: 'Place content',
    type: 'string',
  },
  backgroundColor: {
    title: 'Background color',
    type: 'string',
    format: 'color',
  },
  color: {
    title: 'Text color',
    type: 'string',
    format: 'color',
  },
  textDecorationColor: {
    title: 'Text decoration color (web, iOS)',
    type: 'string',
    format: 'color',
  },
  inactiveColor: {
    title: 'Inactive color',
    type: 'string',
    format: 'color',
  },
  shadowColor: {
    title: 'Shadow color (iOS, web)',
    type: 'string',
    format: 'color',
  },
  textShadowColor: {
    title: 'Text shadow color',
    type: 'string',
    format: 'color',
  },
  borderColor: {
    title: 'Border color',
    type: 'string',
    format: 'color',
  },
  borderLeftColor: {
    title: 'Border left color',
    type: 'string',
    format: 'color',
  },
  borderRightColor: {
    title: 'Border right color',
    type: 'string',
    format: 'color',
  },
  borderTopColor: {
    title: 'Border top color',
    type: 'string',
    format: 'color',
  },
  borderBottomColor: {
    title: 'Border bottom color',
    type: 'string',
    format: 'color',
  },
  overlayColor: {
    title: 'Overlay color (Android)',
    type: 'string',
    format: 'color',
  },
  tintColor: {
    title: 'Tint color (Android, iOS)',
    type: 'string',
    format: 'color',
  },
  fontFamily: {
    title: 'Font',
    type: 'fontFamily',
  },
  fontWeight: {
    title: 'Font weight',
    anyOf: fontWeights.map(({ value, label }) => ({
      title: label,
      type: 'string',
      enum: [value],
    })),
    default: 'normal',
  },
  fontStyle: {
    title: 'Font style',
    anyOf: fontStyles.map(({ value, label }) => ({
      title: label,
      type: 'string',
      enum: [value],
    })),
    default: 'normal',
  },
  textDecorationLine: {
    title: 'Text decoration line',
    type: 'string',
    enum: ['none', 'underline', 'line-through'],
    default: 'none',
  },
  textAlign: {
    title: 'Text align',
    default: 'left',
    anyOf: [
      {
        type: 'string',
        title: 'Left',
        enum: ['left'],
      },
      {
        type: 'string',
        title: 'Right',
        enum: ['right'],
      },
      {
        type: 'string',
        title: 'Center',
        enum: ['center'],
      },
      {
        type: 'string',
        title: 'Justify',
        enum: ['justify'],
      },
    ],
  },
  textAlignVertical: {
    title: 'Text vertical align (web, Android)',
    type: 'string',
    enum: ['auto', 'top', 'center', 'bottom'],
    default: 'auto',
  },
  includeFontPadding: {
    title: 'Include font padding (Android)',
    type: 'string',
    enum: ['true', 'false'],
    default: 'true',
  },
  fontVariant: {
    title: 'Font variant (web, iOS)',
    type: 'string',
    enum: ['normal', 'small-caps', 'oldstyle-nums', 'lining-nums', 'tabular-nums', 'proportional-nums'],
    default: 'normal',
  },
  textDecorationStyle: {
    title: 'Text decoration style (web, iOS)',
    type: 'string',
    enum: ['solid', 'double', 'dotted', 'dashed'],
    default: 'solid',
  },
  textTransform: {
    title: 'Text transform',
    type: 'string',
    enum: ['none', 'uppercase', 'lowercase', 'capitalize'],
    default: 'none',
  },
  shadowEnabled: {
    title: 'Shadow enabled (iOS, web)',
    type: 'boolean',
    default: false,
  },
  textShadowEnabled: {
    title: 'Text shadow enabled',
    type: 'boolean',
    default: false,
  },
  shadowOffsetHeight: {
    title: 'Shadow offset Y (iOS, web)',
    type: 'number',
    default: 0,
  },
  shadowOffsetWidth: {
    title: 'Shadow offset X (iOS, web)',
    type: 'number',
    default: 0,
  },
  textShadowOffsetHeight: {
    title: 'Text shadow offset Y',
    type: 'number',
    default: 0,
  },
  textShadowOffsetWidth: {
    title: 'Text shadow offset X',
    type: 'number',
    default: 0,
  },
  shadowRadius: {
    title: 'Shadow blur radius (iOS, web)',
    type: 'number',
    default: 0,
    // NOTE: `step` is not a schema keyword and does not affect type validation.
    // It is only used by the UI component for correct stepper
    step: 0.1,
  },
  textShadowRadius: {
    title: 'Text shadow radius',
    type: 'number',
    default: 0,
    // NOTE: `step` is not a schema keyword and does not affect type validation.
    // It is only used by the UI component for correct stepper
    step: 0.1,
  },
  borderStyle: {
    title: 'Border style',
    type: 'string',
    enum: ['none', 'solid', 'dotted', 'dashed'],
    default: 'none',
  },
  resizeMode: {
    title: 'Image resize mode (Android, iOS)',
    type: 'string',
    enum: ['contain', 'cover', 'stretch', 'center', 'repeat'],
  },
  borderWidth: {
    title: 'Border width',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderLeftWidth: {
    title: 'Border left width',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderRightWidth: {
    title: 'Border right width',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderTopWidth: {
    title: 'Border top width',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderBottomWidth: {
    title: 'Border bottom width',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderRadius: {
    title: 'Border radius',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderTopLeftRadius: {
    title: 'Border top left radius',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderTopRightRadius: {
    title: 'Border top right radius',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderBottomLeftRadius: {
    title: 'Border bottom left radius',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  borderBottomRightRadius: {
    title: 'Border bottom right radius',
    type: 'number',
    minimum: 0,
    default: 0,
  },
  display: {
    title: 'Display',
    type: 'string',
    enum: ['flex', 'none'],
    default: 'flex',
  },
  position: {
    title: 'Position',
    type: 'string',
    enum: ['relative', 'absolute'],
    default: 'relative',
  },
  overflow: {
    title: 'Overflow',
    type: 'string',
    enum: ['visible', 'hidden', 'scroll'],
    default: 'visible',
  },
  backfaceVisibility: {
    title: 'Backface visibility',
    type: 'string',
    enum: ['visible', 'hidden'],
    default: 'visible',
  },
  scrollDisabled: {
    // Special style only for pages
    title: 'Disable scrolling',
    type: 'boolean',
  },
  safeAreaDisabled: {
    // Special style only for pages
    title: 'Disable safe area',
    type: 'boolean',
  },
  transform: {
    title: 'Transform',
    type: 'json',
  },
  gapEnabled: {
    title: 'Enable gap',
    type: 'boolean',
  },
  gapAfter: {
    title: 'Gap after',
    type: 'number',
  },
  dimensionsBehavior: {
    type: 'string',
    enum: ['expandWidth', 'fitToContent', 'exactSize', 'custom', 'advanced'],
  },
  heightBehavior: {
    type: 'string',
    enum: ['expandToFill', 'fitToContent', 'setValue'],
  },
  widthBehavior: {
    type: 'string',
    enum: ['expandToFill', 'fitToContent', 'setValue'],
  },
} satisfies Record<string, Schema>;

export type StylePropertySchemas = typeof stylePropertySchemas;

export const stylePropertyNames = getKeys(stylePropertySchemas);

export function getStylePropertySchema(styleProperty: keyof StylePropertySchemas): Schema;
export function getStylePropertySchema(styleProperty: StyleProperty): Schema | undefined;
export function getStylePropertySchema(styleProperty: StyleProperty): Schema | undefined {
  const schemas: Record<string, Schema | undefined> = stylePropertySchemas;
  return schemas[styleProperty];
}

/**
 * List of all style property names that should be ignored by style classes
 * and that are editable on the "Layout" tab.
 */
export const layoutStylePropertyNames: StyleProperty[] = [
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginBottom',
  'flexDirection',
  'justifyContent',
  'alignItems',
  'flexWrap',
  'alignContent',
  'placeContent',
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'overflow',
  'flex',
  'alignSelf',
  'position',
  'left',
  'top',
  'right',
  'bottom',
  'zIndex',
  'gapAfter',
  'gapEnabled',
  'dimensionsBehavior',
  'heightBehavior',
  'widthBehavior',
  'textAlign',
];

export const individualBorderRadiusStylePropertyNames: StyleProperty[] = [
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
];

export const shapeStylePropertyNames: StyleProperty[] = ['borderRadius', ...individualBorderRadiusStylePropertyNames];

export const shadowSizeStylePropertyNames: StyleProperty[] = [
  'shadowOffsetWidth',
  'shadowOffsetHeight',
  'shadowRadius',
];

export const boxShadowStylePropertyNames: StyleProperty[] = [
  'shadowEnabled',
  ...shadowSizeStylePropertyNames,
  'shadowColor',
];

const individualBorderWidthStylePropertyNames: StyleProperty[] = [
  'borderBottomWidth',
  'borderLeftWidth',
  'borderRightWidth',
  'borderTopWidth',
];

export const borderStylePropertyNames: StyleProperty[] = [
  'borderColor',
  'borderStyle',
  'borderWidth',
  ...individualBorderWidthStylePropertyNames,
];

export const paddingPropertyNames: StyleProperty[] = [
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
];

export type StyleProperty = keyof StyleBindings;

export type ResolvedStyle = { [styleProp in StyleProperty]?: unknown };

export type CssProperty = keyof CSSProperties | 'inactiveColor';

export type ResolvedCss = { [cssProperty in CssProperty]?: string | number };
